import React, { useEffect, useRef } from "react";
import { configData } from "../config";

// The editor the live site's edit forms use: the Drupal ckeditor module (7.x-1.23) loads
// CKEditor 4.21.0, full build, from the CKEditor CDN
const CKEDITOR_SRC = "https://cdn.ckeditor.com/4.21.0/full-all/ckeditor.js";

let loading = null;
const loadCkeditor = () => {
  if (window.CKEDITOR) return Promise.resolve(window.CKEDITOR);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = CKEDITOR_SRC;
      script.onload = () => {
        // The module's css/ckeditor.css, which the live site adds to the editing area
        window.CKEDITOR.addCss(
          ".rteindent1{margin-left:40px}.rteindent2{margin-left:80px}.rteindent3{margin-left:120px}" +
          ".rteindent4{margin-left:160px}.rteleft{text-align:left}.rteright{text-align:right}" +
          ".rtecenter{text-align:center}.rtejustify{text-align:justify}" +
          ".ibimage_left{float:left}.ibimage_right{float:right}"
        );
        resolve(window.CKEDITOR);
      };
      script.onerror = () => {
        loading = null;
        reject(new Error("CKEditor could not be loaded"));
      };
      document.head.appendChild(script);
    });
  }
  return loading;
};

// The live site's toolbar (its "Full HTML" profile)
const TOOLBAR = [
  ["Source"],
  ["Cut", "Copy", "Paste", "PasteText", "PasteFromWord", "-", "Scayt"],
  ["Undo", "Redo"],
  ["Image", "Table", "HorizontalRule", "Smiley", "SpecialChar"],
  ["Maximize", "ShowBlocks"],
  "/",
  ["Format"],
  ["Bold", "Italic", "Underline", "Strike", "-", "Subscript", "Superscript", "-", "RemoveFormat"],
  ["NumberedList", "BulletedList", "-", "Outdent", "Indent", "Blockquote"],
  ["JustifyLeft", "JustifyCenter", "JustifyRight", "JustifyBlock", "-", "BidiLtr", "BidiRtl"],
  ["Link", "Unlink", "Anchor"],
];

// The sports smileys the live site's sites/all/modules/ckeditor/ckeditor.config.js adds
const SMILEY_IMAGES = [
  "regular_smile.png", "sad_smile.png", "wink_smile.png", "teeth_smile.png", "confused_smile.png", "tongue_smile.png",
  "embarrassed_smile.png", "omg_smile.png", "whatchutalkingabout_smile.png", "angry_smile.png", "angel_smile.png", "shades_smile.png",
  "devil_smile.png", "cry_smile.png", "lightbulb.png", "thumbs_down.png", "thumbs_up.png", "heart.png",
  "broken_heart.png", "kiss.png", "envelope.png", "Airplane.png", "American_Football.png", "Bar_Chart.png", "Baseball.png", "Basketball.png", "Brain.png",
  "Briefcase.png", "Chart_Increasing.png", "Check_Mark_Button.png", "Cricket_Game.png", "Direct_Hit.png", "Fire.png", "Graduation_Cap.png", "Ice_Hockey.png",
  "Police_Car_Light.png", "Soccer_Ball.png", "Sports_Medal.png", "Trophy.png", "hand_icon.png",
];

// A rich text field as the live site's Drupal forms show it. `rows` is the textarea's row count,
// which the module turns into the editing area's height (20px a row). The editor keeps its own
// content after it starts, so `value` is only read then; changes come back through onChange.
const CkEditor = ({ value, onChange, rows = 20 }) => {
  const textareaRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const startValue = useRef(value);

  useEffect(() => {
    let editor = null;
    let cancelled = false;
    loadCkeditor().then((CKEDITOR) => {
      if (cancelled || !textareaRef.current) return;
      editor = CKEDITOR.replace(textareaRef.current, {
        toolbar: TOOLBAR,
        width: "100%",
        height: rows * 20 + "px",
        resize_minWidth: 450,
        enterMode: CKEDITOR.ENTER_P,
        shiftEnterMode: CKEDITOR.ENTER_BR,
        format_tags: "p;div;pre;address;h1;h2;h3;h4;h5;h6",
        // Full HTML: keep every tag, style and attribute the stored pages use
        allowedContent: true,
        entities: false,
        entities_greek: false,
        entities_latin: false,
        scayt_autoStartup: false,
        indentClasses: ["rteindent1", "rteindent2", "rteindent3", "rteindent4"],
        justifyClasses: ["rteleft", "rtecenter", "rteright", "rtejustify"],
        // Drupal pages keep PHP in their bodies; keep it as it is rather than as text
        protectedSource: [/<\?[\s\S]*?\?>/g],
        smiley_path: configData.DRUPAL_LIVE_DOMAIN_URL + "/sites/all/modules/ckeditor/smiley/",
        smiley_images: SMILEY_IMAGES,
        // This CDN build warns that 4.21 is out of date everywhere but on Drupal pages
        versionCheck: false,
        // Bodies point at Drupal files with "/sites/..." addresses, which live's editor loads from
        // its own domain. Here they come from the Drupal copy, as on the page itself (Body.js)
        baseHref: configData.DRUPAL_DOMAIN_URL + "/",
      });
      // Also as on the page: a picture missing from the Drupal copy is shown from the live site.
      // Only the picture shown changes; CKEditor saves the address the body has.
      const copyFiles = configData.DRUPAL_DOMAIN_URL + "/sites/";
      const liveFiles = configData.DRUPAL_LIVE_DOMAIN_URL + "/sites/";
      const showFromLive = (img) => {
        if (!img.src.startsWith(copyFiles)) return;
        const wasDirty = editor.checkDirty();
        img.src = liveFiles + img.src.slice(copyFiles.length);
        if (!wasDirty) editor.resetDirty();
      };
      editor.on("contentDom", () => {
        const doc = editor.document.$;
        doc.addEventListener("error", (e) => { if (e.target.tagName === "IMG") showFromLive(e.target); }, true);
        doc.querySelectorAll("img").forEach((img) => { if (img.complete && img.naturalWidth === 0) showFromLive(img); });
      });
      const emit = () => onChangeRef.current(editor.getData());
      editor.on("change", emit);
      // Text that reaches the editing area without key presses (dictation, spelling fixes)
      // gets no "change" event, nor does typing in Source mode, so their input events count too
      const emitOnInput = () => {
        const editable = editor.editable();
        if (editable) editable.attachListener(editable, "input", emit);
      };
      editor.on("contentDom", emitOnInput);
      editor.on("mode", () => {
        if (editor.mode === "source") emitOnInput();
      });
    });
    return () => {
      cancelled = true;
      if (editor) {
        // Hand back what was typed last, for the plain text editor that replaces this one
        if (editor.checkDirty()) onChangeRef.current(editor.getData());
        editor.destroy(true);
      }
    };
  }, [rows]);

  // CKEditor puts its own markup next to the textarea, so keep both inside a div of ours
  return (
    <div className="ckeditor-field">
      <textarea ref={textareaRef} defaultValue={startValue.current} rows={rows} style={{ visibility: "hidden" }} />
    </div>
  );
};

export default CkEditor;
